import { useMemo } from 'react'

import {
  useFilters, useModals, useSetState, useShowModal,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateTeamModal from '@modals/CreateTeamModal'

import { TeamModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateTeamModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateTeamModal: {
      closeModal: () => setState({ showCreateTeamModal: false }),
      createTeam: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateTeamModal } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCreateTeamModal: (payload: { team: Partial<TeamModel> }) => {
        setModalData('CreateTeamModal', payload)
        setState({ showCreateTeamModal: true })
      },
      ...filterCallbacks,
    },
    defaultFilters,
  }), [])

  useShowModal({
    modalKey: 'CreateTeamModal',
    options: {
      callbacks: pageContext.callbacks,
    },
  })

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateTeamModal
          callbacks={callbacks('CreateTeamModal', setState)}
          showModal={showCreateTeamModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
